import React, { useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import styled from 'styled-components';
import { Input, Button, Dropdown, message } from 'antd';
import { useSelector } from 'react-redux';
import { RequestStatus } from '@lib/plugin-redux-core';
import { AppModal } from '@lib/common';
import { SendOutlined, StopOutlined, UploadOutlined, PlusOutlined, FolderOutlined, LinkOutlined, SettingOutlined, CheckOutlined } from '@ant-design/icons';
import { useCreditBalance, useLlmProviders, usePromptInput, useUserSendMessage } from './hooks';
import { AutoGrowingTextarea } from '../../components';
import { selectError, selectSendMessageStatus } from '../../redux/ai-chat/ai-chat.selectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var REST_MODEL_ERROR_MESSAGES = {
  LLM_MODEL_PLAN_NOT_ALLOWED: 'Your current plan cannot use this model. Choose another model.',
  LLM_MODEL_FILE_EXTENSION_NOT_ALLOWED: 'One or more attached files are not supported by this model.',
  LLM_MODEL_FILE_SIZE_LIMIT_EXCEEDED: 'One or more attached files exceed this model’s per-file limit.',
  LLM_MODEL_TOTAL_SIZE_LIMIT_EXCEEDED: 'Attached files exceed this model’s total size limit.'
};

var getErrorCode = function getErrorCode(error) {
  var _JSON$stringify;

  var serialized = typeof error === 'string' ? error : (_JSON$stringify = JSON.stringify(error !== null && error !== void 0 ? error : {})) !== null && _JSON$stringify !== void 0 ? _JSON$stringify : '';
  return Object.keys(REST_MODEL_ERROR_MESSAGES).find(function (code) {
    return serialized.includes(code);
  });
}; // ─── Model Config ─────────────────────────────────────────────────────────────
// ─── Styled Components ────────────────────────────────────────────────────────


var PromptInputWrapper = styled.div.withConfig({
  displayName: "prompt-inputcontainer__PromptInputWrapper",
  componentId: "sc-11v29i7-0"
})(["flex-shrink:0;padding:12px;border-radius:16px;margin:12px;display:flex;flex-direction:column;gap:4px;border:1px solid #d6d6d6;"]);
var PromptInputStyled = styled(Input.TextArea).withConfig({
  displayName: "prompt-inputcontainer__PromptInputStyled",
  componentId: "sc-11v29i7-1"
})(["flex:1;background:transparent !important;border:none !important;box-shadow:none !important;padding:8px 4px !important;font-size:15px !important;resize:none;&::placeholder{color:#8b95b0 !important;}&:hover,&:focus{background:transparent !important;outline:none !important;box-shadow:none !important;}"]);
var ActionsRow = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ActionsRow",
  componentId: "sc-11v29i7-2"
})(["display:flex;align-items:center;justify-content:space-between;width:100%;"]);
var LeftActions = styled.div.withConfig({
  displayName: "prompt-inputcontainer__LeftActions",
  componentId: "sc-11v29i7-3"
})(["display:flex;align-items:center;gap:4px;"]);
var DropdownItem = styled.div.withConfig({
  displayName: "prompt-inputcontainer__DropdownItem",
  componentId: "sc-11v29i7-4"
})(["display:flex;align-items:center;gap:12px;width:100%;padding:8px 12px;border-radius:8px;cursor:pointer;transition:all 0.2s ease;color:#1a2140;&:hover{background:#f0f2f5;}"]);
var ItemIconWrapper = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ItemIconWrapper",
  componentId: "sc-11v29i7-5"
})(["display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:6px;background:#f0f2f5;color:#1a2140;flex-shrink:0;svg{font-size:14px;}"]);
var ItemTextGroup = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ItemTextGroup",
  componentId: "sc-11v29i7-6"
})(["display:flex;flex-direction:column;"]);
var ItemTitle = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ItemTitle",
  componentId: "sc-11v29i7-7"
})(["font-size:14px;font-weight:600;line-height:1.2;"]);
var ItemDescription = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ItemDescription",
  componentId: "sc-11v29i7-8"
})(["font-size:12px;color:#8b95b0;line-height:normal;"]); // ─── Toolbar Icon Button (shared style for all toolbar icons) ─────────────────

var ToolbarIconButton = styled(Button).withConfig({
  displayName: "prompt-inputcontainer__ToolbarIconButton",
  componentId: "sc-11v29i7-9"
})(["display:inline-flex !important;align-items:center !important;justify-content:center !important;width:30px !important;height:30px !important;min-width:30px !important;padding:0 !important;border-radius:8px !important;background:transparent !important;color:#6b7280 !important;border:none !important;box-shadow:none !important;cursor:pointer;flex-shrink:0;transition:all 0.15s ease;&:hover:not(:disabled){background:#f0f2f5 !important;color:#2a324d !important;}&:active:not(:disabled){transform:scale(0.93);}&:disabled{opacity:0.4 !important;cursor:not-allowed !important;}svg{font-size:15px;}"]); // ─── Send / Stop Icon Button ──────────────────────────────────────────────────

var SendIconButton = styled(Button).withConfig({
  displayName: "prompt-inputcontainer__SendIconButton",
  componentId: "sc-11v29i7-10"
})(["display:inline-flex !important;align-items:center !important;justify-content:center !important;width:30px !important;height:30px !important;min-width:30px !important;padding:0 !important;border-radius:50% !important;background:", " !important;border:none !important;color:#ffffff !important;cursor:pointer;flex-shrink:0;transition:all 0.15s ease;&:hover:not(:disabled){opacity:0.85;}&:active:not(:disabled){transform:scale(0.93);}&:disabled{opacity:0.4 !important;cursor:not-allowed !important;}> span{display:flex !important;align-items:center !important;justify-content:center !important;width:100%;height:100%;line-height:1;}svg{font-size:14px;display:block;}"], function (p) {
  return p.$danger ? '#ff4d4f' : '#1D90FF';
}); // ─── Credit Ring ──────────────────────────────────────────────────────────────

var getProgressColor = function getProgressColor(percent) {
  if (percent >= 60) return '#52C41A';
  if (percent >= 30) return '#FAAD14';
  if (percent >= 10) return '#FF8C00';
  return '#FF4D4F';
};

var CreditRingWrapper = styled.div.withConfig({
  displayName: "prompt-inputcontainer__CreditRingWrapper",
  componentId: "sc-11v29i7-11"
})(["position:relative;display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;cursor:default;flex-shrink:0;"]);
var CreditHoverLabel = styled.div.withConfig({
  displayName: "prompt-inputcontainer__CreditHoverLabel",
  componentId: "sc-11v29i7-12"
})(["position:absolute;right:calc(100% + 8px);top:50%;transform:translateY(-50%) translateX(4px);display:inline-flex;flex-direction:column;align-items:flex-start;gap:2px;min-height:40px;padding:7px 10px;border-radius:12px;background:#111827;color:#ffffff;white-space:nowrap;box-shadow:0 8px 24px rgba(17,24,39,0.18);opacity:0;visibility:hidden;pointer-events:none;transition:opacity 0.18s ease,transform 0.18s ease,visibility 0.18s ease;", ":hover &{opacity:1;visibility:visible;transform:translateY(-50%) translateX(0);}"], CreditRingWrapper);
var CreditHoverTitle = styled.span.withConfig({
  displayName: "prompt-inputcontainer__CreditHoverTitle",
  componentId: "sc-11v29i7-13"
})(["font-size:11px;font-weight:600;line-height:1.1;"]);
var CreditHoverMeta = styled.span.withConfig({
  displayName: "prompt-inputcontainer__CreditHoverMeta",
  componentId: "sc-11v29i7-14"
})(["font-size:10px;line-height:1.1;color:rgba(255,255,255,0.82);"]);
var CreditRingVisual = styled.div.withConfig({
  displayName: "prompt-inputcontainer__CreditRingVisual",
  componentId: "sc-11v29i7-15"
})(["display:inline-flex;align-items:center;justify-content:center;width:22px;height:22px;"]);

var CreditRing = function CreditRing(_ref) {
  var percent = _ref.percent,
      color = _ref.color,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 18 : _ref$size;
  var strokeWidth = 2.5;
  var radius = (size - strokeWidth) / 2;
  var circumference = 2 * Math.PI * radius;
  var offset = circumference - percent / 100 * circumference;
  return /*#__PURE__*/_jsxs("svg", {
    width: size,
    height: size,
    style: {
      transform: 'rotate(-90deg)',
      display: 'block'
    },
    children: [/*#__PURE__*/_jsx("circle", {
      cx: size / 2,
      cy: size / 2,
      r: radius,
      fill: "none",
      stroke: "rgba(0,0,0,0.08)",
      strokeWidth: strokeWidth
    }), /*#__PURE__*/_jsx("circle", {
      cx: size / 2,
      cy: size / 2,
      r: radius,
      fill: "none",
      stroke: color,
      strokeWidth: strokeWidth,
      strokeDasharray: circumference,
      strokeDashoffset: offset,
      strokeLinecap: "round",
      style: {
        transition: 'stroke-dashoffset 0.4s ease'
      }
    })]
  });
};

var RightActions = styled.div.withConfig({
  displayName: "prompt-inputcontainer__RightActions",
  componentId: "sc-11v29i7-16"
})(["display:flex;align-items:center;gap:6px;"]); // ─── Model Selector Styled ───────────────────────────────────────────────────

var ModelMenuItem = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ModelMenuItem",
  componentId: "sc-11v29i7-17"
})(["display:flex;align-items:center;gap:10px;padding:8px 12px;border-radius:8px;cursor:pointer;transition:background 0.15s ease;color:#1a2140;&:hover{background:#f0f2f5;}"]); // ─── Model Selector Button (icon + label) ────────────────────────────────────

var ModelSelectorButton = styled(Button).withConfig({
  displayName: "prompt-inputcontainer__ModelSelectorButton",
  componentId: "sc-11v29i7-18"
})(["display:inline-flex !important;align-items:center !important;height:30px !important;padding:0 8px 0 8px !important;border-radius:8px !important;background:transparent !important;color:#6b7280 !important;border:none !important;box-shadow:none !important;cursor:pointer;flex-shrink:0;transition:all 0.15s ease;&:hover:not(:disabled){background:#f0f2f5 !important;color:#2a324d !important;}&:active:not(:disabled){transform:scale(0.93);}&:disabled{opacity:0.4 !important;cursor:not-allowed !important;}svg{font-size:14px;}"]);
var ModelLabelText = styled.span.withConfig({
  displayName: "prompt-inputcontainer__ModelLabelText",
  componentId: "sc-11v29i7-19"
})(["font-size:12px;font-weight:500;line-height:1;white-space:nowrap;"]);
var ModelInfo = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ModelInfo",
  componentId: "sc-11v29i7-20"
})(["display:flex;flex-direction:column;flex:1;"]);
var ModelName = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ModelName",
  componentId: "sc-11v29i7-21"
})(["font-size:13px;font-weight:600;line-height:1.3;"]);
var ModelDesc = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ModelDesc",
  componentId: "sc-11v29i7-22"
})(["font-size:11px;color:#8b95b0;line-height:1.3;"]);
var ModelCheck = styled.div.withConfig({
  displayName: "prompt-inputcontainer__ModelCheck",
  componentId: "sc-11v29i7-23"
})(["width:18px;display:flex;align-items:center;justify-content:center;color:#1d90ff;font-size:12px;flex-shrink:0;"]); // ─── Component ────────────────────────────────────────────────────────────────

export var PromptInputContainer = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var _selectedModel$displa;

  var conversationId = _ref2.conversationId,
      streamCallbacks = _ref2.streamCallbacks,
      _ref2$isStreaming = _ref2.isStreaming,
      isStreaming = _ref2$isStreaming === void 0 ? false : _ref2$isStreaming,
      _ref2$disabled = _ref2.disabled,
      disabled = _ref2$disabled === void 0 ? false : _ref2$disabled,
      _ref2$placeholder = _ref2.placeholder,
      placeholder = _ref2$placeholder === void 0 ? 'Type a message or paste a link...' : _ref2$placeholder,
      onBuildingDataBeforeSendMessage = _ref2.onBuildingDataBeforeSendMessage,
      _ref2$clearOnSubmit = _ref2.clearOnSubmit,
      clearOnSubmit = _ref2$clearOnSubmit === void 0 ? true : _ref2$clearOnSubmit,
      onOpenUploadModal = _ref2.onOpenUploadModal,
      onStopRequested = _ref2.onStopRequested,
      onSnapshotMentionResources = _ref2.onSnapshotMentionResources,
      _ref2$enableAutoResiz = _ref2.enableAutoResize,
      enableAutoResize = _ref2$enableAutoResiz === void 0 ? false : _ref2$enableAutoResiz,
      _ref2$enableStopConfi = _ref2.enableStopConfirmation,
      enableStopConfirmation = _ref2$enableStopConfi === void 0 ? false : _ref2$enableStopConfi,
      onStopConfirmed = _ref2.onStopConfirmed,
      onStopCompleted = _ref2.onStopCompleted,
      onBeforeSend = _ref2.onBeforeSend,
      walletPercent = _ref2.walletPercent,
      _ref2$walletLoading = _ref2.walletLoading,
      walletLoading = _ref2$walletLoading === void 0 ? false : _ref2$walletLoading,
      onAfterStreamEnd = _ref2.onAfterStreamEnd,
      extraOptions = _ref2.extraOptions,
      extraFiles = _ref2.extraFiles,
      userPlanId = _ref2.userPlanId,
      onModelChange = _ref2.onModelChange;
  var attachBtnRef = useRef(null);
  var sendMessageStatus = useSelector(selectSendMessageStatus);
  var sendMessageError = useSelector(selectError);

  var _useLlmProviders = useLlmProviders(),
      models = _useLlmProviders.models,
      isLoadingModels = _useLlmProviders.isLoading,
      isErrorModels = _useLlmProviders.isError;

  var _useState = useState(false),
      dropdownOpen = _useState[0],
      setDropdownOpen = _useState[1];

  var _useState2 = useState(false),
      modelDropdownOpen = _useState2[0],
      setModelDropdownOpen = _useState2[1];

  var _useState3 = useState(false),
      isStopping = _useState3[0],
      setIsStopping = _useState3[1];

  var _useState4 = useState(),
      selectedModelName = _useState4[0],
      setSelectedModelName = _useState4[1];

  var isModelAllowedForPlan = useCallback(function (model) {
    if (model.applicableUserPlans.length === 0) return true;
    return Boolean(userPlanId && model.applicableUserPlans.some(function (planId) {
      return planId.toLowerCase() === userPlanId.toLowerCase();
    }));
  }, [userPlanId]);
  var enabledModels = useMemo(function () {
    return models.filter(isModelAllowedForPlan);
  }, [isModelAllowedForPlan, models]);
  useEffect(function () {
    if (!enabledModels.length) {
      setSelectedModelName(undefined);
      return;
    }

    var hasSelectedModel = selectedModelName ? enabledModels.some(function (model) {
      return model.name === selectedModelName;
    }) : false;

    if (!hasSelectedModel) {
      var _enabledModels$find;

      setSelectedModelName(((_enabledModels$find = enabledModels.find(function (model) {
        return model.isDefault;
      })) !== null && _enabledModels$find !== void 0 ? _enabledModels$find : enabledModels[0]).name);
    }
  }, [enabledModels, selectedModelName]);
  var selectedModel = useMemo(function () {
    var _enabledModels$find2;

    return (_enabledModels$find2 = enabledModels.find(function (model) {
      return model.name === selectedModelName;
    })) !== null && _enabledModels$find2 !== void 0 ? _enabledModels$find2 : enabledModels[0];
  }, [enabledModels, selectedModelName]);
  var canAttachFiles = Boolean(selectedModel && selectedModel.allowedFileExtensions.length > 0 && selectedModel.fileSizeLimit > 0 && selectedModel.totalSizeLimit > 0);
  var llmConfig = useMemo(function () {
    return selectedModel ? {
      providerId: selectedModel.providerId,
      modelId: selectedModel.name,
      thinkingLevel: 'low'
    } : undefined;
  }, [selectedModel]);
  useEffect(function () {
    onModelChange === null || onModelChange === void 0 ? void 0 : onModelChange(selectedModel ? {
      allowedFileExtensions: selectedModel.allowedFileExtensions,
      fileSizeLimit: selectedModel.fileSizeLimit,
      totalSizeLimit: selectedModel.totalSizeLimit
    } : null);
  }, [onModelChange, selectedModel]);

  var _useUserSendMessage = useUserSendMessage({
    conversationId: conversationId,
    streamCallbacks: streamCallbacks,
    onBuildingDataBeforeSendMessage: onBuildingDataBeforeSendMessage,
    onSnapshotMentionResources: onSnapshotMentionResources,
    llmConfig: llmConfig,
    extraFiles: extraFiles,
    fileRules: selectedModel ? {
      allowedFileExtensions: selectedModel.allowedFileExtensions,
      fileSizeLimit: selectedModel.fileSizeLimit,
      totalSizeLimit: selectedModel.totalSizeLimit
    } : undefined
  }),
      sendMessageRaw = _useUserSendMessage.sendMessage;

  var sendMessage = useCallback(function (content) {
    var didSend = sendMessageRaw(content);
    if (didSend) onBeforeSend === null || onBeforeSend === void 0 ? void 0 : onBeforeSend();
    return didSend;
  }, [onBeforeSend, sendMessageRaw]);
  var lastSubmittedContentRef = useRef(null);
  var sendMessageWithSnapshot = useCallback(function (content) {
    var didSend = sendMessage(content);
    if (didSend) lastSubmittedContentRef.current = content;
    return didSend;
  }, [sendMessage]);
  var credit = useCreditBalance();
  var fallbackRefetchCredit = credit.refetch;
  var hasWalletProps = walletPercent !== undefined;
  var effectiveWalletPercent = hasWalletProps ? walletPercent : credit.percent;
  var effectiveWalletLoading = hasWalletProps ? walletLoading : credit.loading;
  var effectiveRefreshAt = hasWalletProps ? null : credit.refreshAt;
  var formatRefreshAt = useCallback(function (timestamp) {
    if (!timestamp) {
      return null;
    }

    var ms = timestamp.length <= 10 ? Number(timestamp) * 1000 : Number(timestamp);

    if (Number.isNaN(ms)) {
      return timestamp;
    }

    return new Date(ms).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []); // Notify parent when streaming ends so it can refetch wallet balance

  var prevStreamingRef = useRef(isStreaming);
  var afterStreamEndTimeoutRef = useRef(null);
  useEffect(function () {
    if (prevStreamingRef.current && !isStreaming) {
      if (afterStreamEndTimeoutRef.current) {
        clearTimeout(afterStreamEndTimeoutRef.current);
      }

      afterStreamEndTimeoutRef.current = setTimeout(function () {
        if (onAfterStreamEnd) {
          onAfterStreamEnd();
        } else {
          fallbackRefetchCredit();
        }

        afterStreamEndTimeoutRef.current = null;
      }, 1000);
    }

    if (isStreaming && afterStreamEndTimeoutRef.current) {
      clearTimeout(afterStreamEndTimeoutRef.current);
      afterStreamEndTimeoutRef.current = null;
    }

    prevStreamingRef.current = isStreaming;
    return function () {
      if (afterStreamEndTimeoutRef.current) {
        clearTimeout(afterStreamEndTimeoutRef.current);
        afterStreamEndTimeoutRef.current = null;
      }
    };
  }, [isStreaming, onAfterStreamEnd, fallbackRefetchCredit]);

  var _usePromptInput = usePromptInput({
    onSubmit: sendMessageWithSnapshot,
    disabled: disabled || isStreaming,
    clearOnSubmit: clearOnSubmit
  }),
      value = _usePromptInput.value,
      handleChange = _usePromptInput.handleChange,
      handleSubmit = _usePromptInput.handleSubmit,
      handleKeyDown = _usePromptInput.handleKeyDown,
      setInitialValue = _usePromptInput.setInitialValue;

  useEffect(function () {
    if (sendMessageStatus !== RequestStatus.ERROR) return;
    var code = getErrorCode(sendMessageError);
    if (!code || !lastSubmittedContentRef.current) return;
    setInitialValue(lastSubmittedContentRef.current);
    message.error(REST_MODEL_ERROR_MESSAGES[code]);
  }, [sendMessageError, sendMessageStatus, setInitialValue]);
  var showStopButton = isStreaming; // Expose the setInitialValue function via ref

  useImperativeHandle(ref, function () {
    return {
      setInitialValue: setInitialValue,
      retryLastMessage: function retryLastMessage() {
        if (lastSubmittedContentRef.current) sendMessage(lastSubmittedContentRef.current);
      }
    };
  }, [sendMessage, setInitialValue]);
  var executeStop = useCallback(function () {
    setIsStopping(true);
    onStopConfirmed === null || onStopConfirmed === void 0 ? void 0 : onStopConfirmed();
    onStopRequested === null || onStopRequested === void 0 ? void 0 : onStopRequested();
    setTimeout(function () {
      setIsStopping(false);
      onStopCompleted === null || onStopCompleted === void 0 ? void 0 : onStopCompleted();
    }, 100);
  }, [onStopConfirmed, onStopRequested, onStopCompleted]);
  var handleStopClick = useCallback(function () {
    // Prevent multiple stop attempts
    if (isStopping || !isStreaming) {
      return;
    }

    if (enableStopConfirmation) {
      AppModal.confirm({
        title: 'Stop Processing',
        content: 'Are you sure you want to stop the AI processing?',
        okText: 'Stop',
        cancelText: 'Cancel',
        okType: 'danger',
        onOk: executeStop
      });
    } else {
      executeStop();
    }
  }, [isStopping, isStreaming, enableStopConfirmation, executeStop]);
  var handleOpenFileSelector = useCallback(function (tab) {
    setDropdownOpen(false);
    onOpenUploadModal === null || onOpenUploadModal === void 0 ? void 0 : onOpenUploadModal(tab);
  }, [onOpenUploadModal]);
  var items = [{
    key: 'upload',
    label: /*#__PURE__*/_jsxs(DropdownItem, {
      onClick: function onClick() {
        return handleOpenFileSelector('upload');
      },
      children: [/*#__PURE__*/_jsx(ItemIconWrapper, {
        children: /*#__PURE__*/_jsx(UploadOutlined, {})
      }), /*#__PURE__*/_jsx(ItemTextGroup, {
        children: /*#__PURE__*/_jsx(ItemTitle, {
          children: "Upload file"
        })
      })]
    })
  }, {
    key: 'library',
    label: /*#__PURE__*/_jsxs(DropdownItem, {
      onClick: function onClick() {
        return handleOpenFileSelector('library');
      },
      children: [/*#__PURE__*/_jsx(ItemIconWrapper, {
        children: /*#__PURE__*/_jsx(FolderOutlined, {})
      }), /*#__PURE__*/_jsx(ItemTextGroup, {
        children: /*#__PURE__*/_jsx(ItemTitle, {
          children: "Choose from library"
        })
      })]
    })
  }, {
    key: 'url',
    label: /*#__PURE__*/_jsxs(DropdownItem, {
      onClick: function onClick() {
        return handleOpenFileSelector('url');
      },
      children: [/*#__PURE__*/_jsx(ItemIconWrapper, {
        children: /*#__PURE__*/_jsx(LinkOutlined, {})
      }), /*#__PURE__*/_jsx(ItemTextGroup, {
        children: /*#__PURE__*/_jsx(ItemTitle, {
          children: "Import via URL"
        })
      })]
    })
  }];

  if (extraOptions && extraOptions.length > 0) {
    items.push({
      type: 'divider'
    });
    extraOptions.forEach(function (option) {
      items.push({
        key: option.value,
        label: /*#__PURE__*/_jsxs(DropdownItem, {
          onClick: function onClick() {
            setDropdownOpen(false);
            option.onClick(option);
          },
          children: [/*#__PURE__*/_jsx(ItemIconWrapper, {
            children: option.icon
          }), /*#__PURE__*/_jsx(ItemTextGroup, {
            children: /*#__PURE__*/_jsx(ItemTitle, {
              children: option.label
            })
          })]
        })
      });
    });
  }

  var modelItems = enabledModels.map(function (model) {
    return {
      key: model.name,
      label: /*#__PURE__*/_jsxs(ModelMenuItem, {
        $active: model.name === (selectedModel === null || selectedModel === void 0 ? void 0 : selectedModel.name),
        onClick: function onClick() {
          setSelectedModelName(model.name);
          setModelDropdownOpen(false);
        },
        children: [/*#__PURE__*/_jsxs(ModelInfo, {
          children: [/*#__PURE__*/_jsx(ModelName, {
            children: model.displayName
          }), /*#__PURE__*/_jsx(ModelDesc, {
            children: model.description
          })]
        }), /*#__PURE__*/_jsx(ModelCheck, {
          children: model.name === (selectedModel === null || selectedModel === void 0 ? void 0 : selectedModel.name) && /*#__PURE__*/_jsx(CheckOutlined, {})
        })]
      })
    };
  });
  return /*#__PURE__*/_jsxs(PromptInputWrapper, {
    children: [enableAutoResize ? /*#__PURE__*/_jsx(AutoGrowingTextarea, {
      value: value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      placeholder: placeholder,
      disabled: disabled,
      enableAutoResize: enableAutoResize,
      style: {
        background: 'transparent !important',
        border: 'none !important',
        boxShadow: 'none !important',
        padding: '8px 4px !important',
        fontSize: '15px !important'
      }
    }) : /*#__PURE__*/_jsx(PromptInputStyled, {
      value: value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      placeholder: placeholder,
      disabled: disabled,
      autoSize: {
        minRows: 1,
        maxRows: 10
      }
    }), /*#__PURE__*/_jsxs(ActionsRow, {
      children: [/*#__PURE__*/_jsxs(LeftActions, {
        children: [/*#__PURE__*/_jsx(Dropdown, {
          menu: {
            items: items
          },
          trigger: ['click'],
          open: dropdownOpen,
          onOpenChange: setDropdownOpen,
          placement: "topLeft",
          dropdownRender: function dropdownRender(menu) {
            return /*#__PURE__*/_jsx("div", {
              style: {
                backgroundColor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                overflow: 'hidden',
                padding: '4px',
                minWidth: '200px'
              },
              children: /*#__PURE__*/React.cloneElement(menu, {
                style: {
                  boxShadow: 'none',
                  border: 'none'
                }
              })
            });
          },
          children: /*#__PURE__*/_jsx(ToolbarIconButton, {
            ref: attachBtnRef,
            disabled: disabled || isStreaming || !canAttachFiles,
            title: (selectedModel === null || selectedModel === void 0 ? void 0 : selectedModel.allowedFileExtensions.length) === 0 ? 'Files are not supported by this model' : !canAttachFiles ? 'File limits are unavailable for this model' : "Attach files (".concat((selectedModel === null || selectedModel === void 0 ? void 0 : selectedModel.allowedFileExtensions.join(', ')) || 'none', ")"),
            "aria-label": "Attach files",
            children: /*#__PURE__*/_jsx(PlusOutlined, {})
          })
        }), /*#__PURE__*/_jsx(Dropdown, {
          menu: {
            items: modelItems
          },
          trigger: ['click'],
          open: modelDropdownOpen,
          onOpenChange: setModelDropdownOpen,
          placement: "topLeft",
          dropdownRender: function dropdownRender(menu) {
            return /*#__PURE__*/_jsx("div", {
              style: {
                backgroundColor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                overflow: 'hidden',
                padding: '4px',
                minWidth: '240px'
              },
              children: /*#__PURE__*/React.cloneElement(menu, {
                style: {
                  boxShadow: 'none',
                  border: 'none'
                }
              })
            });
          },
          children: /*#__PURE__*/_jsxs(ModelSelectorButton, {
            disabled: disabled || isStreaming || !selectedModel && !isErrorModels || isLoadingModels,
            title: isErrorModels ? 'Failed to load models' : selectedModel ? "Model: ".concat(selectedModel.displayName) : 'No model available',
            "aria-label": "Select model",
            loading: isLoadingModels,
            danger: isErrorModels,
            children: [/*#__PURE__*/_jsx(SettingOutlined, {}), /*#__PURE__*/_jsx(ModelLabelText, {
              children: isLoadingModels ? 'Loading models...' : isErrorModels ? 'Failed to load models' : (_selectedModel$displa = selectedModel === null || selectedModel === void 0 ? void 0 : selectedModel.displayName) !== null && _selectedModel$displa !== void 0 ? _selectedModel$displa : 'No model'
            })]
          })
        })]
      }), /*#__PURE__*/_jsxs(RightActions, {
        children: [!effectiveWalletLoading && effectiveWalletPercent !== undefined && effectiveWalletPercent >= 0 && /*#__PURE__*/_jsxs(CreditRingWrapper, {
          title: "".concat(effectiveWalletPercent, "% credits remaining"),
          children: [/*#__PURE__*/_jsxs(CreditHoverLabel, {
            $color: getProgressColor(effectiveWalletPercent),
            children: [/*#__PURE__*/_jsxs(CreditHoverTitle, {
              children: [effectiveWalletPercent, "% remaining"]
            }), formatRefreshAt(effectiveRefreshAt) ? /*#__PURE__*/_jsxs(CreditHoverMeta, {
              children: ["Refresh at ", formatRefreshAt(effectiveRefreshAt)]
            }) : null]
          }), /*#__PURE__*/_jsx(CreditRingVisual, {
            children: /*#__PURE__*/_jsx(CreditRing, {
              percent: effectiveWalletPercent,
              color: getProgressColor(effectiveWalletPercent)
            })
          })]
        }), showStopButton ? /*#__PURE__*/_jsx(SendIconButton, {
          type: "primary",
          $danger: true,
          onClick: handleStopClick,
          disabled: !conversationId || isStopping,
          loading: isStopping,
          title: "Stop",
          "aria-label": "Stop",
          children: /*#__PURE__*/_jsx(StopOutlined, {})
        }) : /*#__PURE__*/_jsx(SendIconButton, {
          type: "primary",
          $danger: false,
          onClick: handleSubmit,
          disabled: disabled || !value.trim(),
          title: "Send message",
          "aria-label": "Send message",
          children: /*#__PURE__*/_jsx(SendOutlined, {})
        })]
      })]
    })]
  });
});
PromptInputContainer.displayName = 'PromptInputContainer';